import React, {useEffect, useMemo, useState} from 'react'
import {Avatar} from '../../common/Avatar'
import {CurrentStateMessageUtils, CurrentStateMessageInput} from './CurrentStateMessageUtils'
import {T} from '../../common/T'
import {LobbyImage, ModeSettingEnum} from '@colonist/types'
import {PlayerColor, TurnState} from '@colonist/game-states'
import {TranslationObject} from '@colonist/i18n'
import {useGameStore} from '../../store/GameStoreContext'
import {usePlayerValidators} from '../../store/selectors/usePlayerValidators'
import {useIsReplay} from '../../store/selectors/useIsReplay'
import clsx from 'clsx'
import styles from './UIGameCurrentStatus.module.scss'
import {useRushCurrentStatus} from '../../store/selectors/useRushCurrentStatus'
import {useIsGameEnded} from '../../store/selectors/useIsGameEnded'

export interface UIGameCurrentStatusProps {
    currentStateMessageInput: CurrentStateMessageInput
    playerDisplayIcon: LobbyImage
    playerDisplayColor: PlayerColor
    className?: string | undefined
    backgroundColor: 'blue' | 'beige'
}

export function UIGameCurrentStatus({
    currentStateMessageInput,
    playerDisplayIcon,
    playerDisplayColor,
    className,
    backgroundColor,
}: UIGameCurrentStatusProps) {
    const isReplay = useIsReplay()
    const myColor = useGameStore((state) => state.myColor)
    const {getPlayerValidator} = usePlayerValidators()
    const defaultShouldShowInitialConnectedText = !isReplay
    const [shouldShowInitialConnectedText, setShouldShowInitialConnectedText] = useState(defaultShouldShowInitialConnectedText)
    const isBeginnerMode = useGameStore((state) => state.gameSettings.modeSetting == ModeSettingEnum.BeginnerModeClassic4P)
    const isRush = useGameStore((state) => state.gameSettings.modeSetting == ModeSettingEnum.ColonistRush4P)
    const rushStatus = useRushCurrentStatus()
    const isInitialPlacement = useGameStore((state) => state.gameState.currentState.turnState == TurnState.InitialPlacement)
    const isGameEnd = useIsGameEnded()

    const classicStatusMessage = useMemo(() => {
        if(shouldShowInitialConnectedText) {
            const connectedText: TranslationObject = {key: 'strings:game.gameState.connectedToGame'}
            return connectedText
        }

        return CurrentStateMessageUtils.getCurrentStateMessage(currentStateMessageInput)
    }, [currentStateMessageInput, shouldShowInitialConnectedText])

    useEffect(() => {
        const timeout = setTimeout(() => setShouldShowInitialConnectedText(false), 3000)
        return () => clearTimeout(timeout)
    }, [])

    const classicStatusIcon = useMemo(() => {
        if(shouldShowInitialConnectedText) {
            if(myColor === PlayerColor.None) {
                return LobbyImage.Players
            }
            const myPlayer = getPlayerValidator(myColor)
            return myPlayer.displayIcon
        }

        return playerDisplayIcon
    }, [playerDisplayIcon, myColor, getPlayerValidator, shouldShowInitialConnectedText])

    const classicStatus = useMemo(() => ({
        text: classicStatusMessage,
        icon: classicStatusIcon,
        color: shouldShowInitialConnectedText ? myColor : playerDisplayColor,
    }), [classicStatusMessage, classicStatusIcon, myColor, playerDisplayColor, shouldShowInitialConnectedText])

    const status = useMemo(() => {
        const isRushStatus = isRush && !isInitialPlacement && !isGameEnd
        if(isRushStatus) return rushStatus
        return classicStatus
    }, [isRush, isInitialPlacement, isGameEnd, classicStatus, rushStatus])

    const hideBackground = status.color === PlayerColor.None
    return (
        <div className={clsx(styles.container, className, styles[backgroundColor], {
            [styles.hidden]: isBeginnerMode,
        })}>
            <Avatar
                className={styles.avatarWrapper}
                color={status.color}
                hideBackground={hideBackground}
                image={status.icon}
            />
            <div className={styles.messageContainer}>
                <T id={status.text} />
            </div>
        </div>
    )
}
